// React
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Redux
import { useSelector } from "react-redux";
import { selectPatient } from "Store/PatientSlice";
import { selectUserEmail } from "Store/AuthSlice";

// Resources
import { provinceOptions } from "Constants/ProvinceConstants";

// MUI
import {
  Card,
  Typography,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Alert,
  Popover,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

// Formik
import { useFormik } from "formik";

// Luxon
import { DateTime } from "luxon";

// Styles
import { styles } from "./styles";
// Components
import ConfirmEditModal from "Common/Modals/ConfirmEditModal/ConfirmEditModal.js";
import { isUnderOneYearOld } from "Common/Components/Formik/UnderOneYearOld";
import {
  FormikDatePicker,
  FormikTextField,
  PatientInfoHeader,
} from "Common/Components";

// Validation
import { patientValidationSchema } from "Validation/PatientValidation";
import {
  isAllZeroPhin,
  mapScannedCardToPatientValues,
} from "Utils/Normalization";
import { formatPatientFormValues } from "Utils/PatientUtils";

// Components
import DuplicatePHINModal from "./DuplicatePHINModal";
import ActivatePatientModal from "./ActivatePatientModal";
import { getPatient } from "API/Resource/patient";

// i18n
import { useTranslation } from "react-i18next";

const PatientForm = () => {
  const { t } = useTranslation(); // <-- i18n Hook
  const hiddenPatient = useSelector(selectPatient);
  const userEmail = useSelector(selectUserEmail);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { patientRef } = state;
  const [confirmModal, setConfirmModal] = useState(false);
  const [openActivatedDialog, setOpenActivatedDialog] = useState(false);
  const [openDuplicatePHINDialog, setOpenDuplicatePHINDialog] = useState(false);
  const [patient, setPatient] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [anchorElPhone, setAnchorElPhone] = useState(null);
  const titleString = patient?.id
    ? "patientForm.titleEdit"
    : "patientForm.titleRegistration";

  useEffect(() => {
    const fetchPatient = async () => {
      setFetching(true);
      if (hiddenPatient?.id) {
        const patient = await getPatient(hiddenPatient.id);
        setPatient(patient);
      } else {
        setPatient(null);
      }
      setFetching(false);
    };
    fetchPatient();
  }, [hiddenPatient]);

  const formik = useFormik({
    initialValues: {
      id: patient?.id,
      firstName: patient?.firstName || "",
      lastName: patient?.lastName || "",
      dob: patient?.dob ? DateTime.fromISO(patient?.dob) : null,
      address: patient?.address || "",
      city: patient?.city || "",
      postalCode: patient?.postalCode || "",
      province: patient?.province || "MB",
      primaryPhone: patient?.primaryPhone || "",
      secondaryPhone: patient?.secondaryPhone || "",
      email: patient?.email || "",
      preferredLanguage: patient?.preferredLanguage || "",
      noPhoneAcknowledgement:
        Boolean(patient?.id) &&
        !(patient?.primaryPhone || patient?.secondaryPhone),
      healthCardNumber: patient?.healthCardNumber || "",
      registrationNumber: patient?.registrationNumber || "",
      healthCardProvince: patient?.healthCardProvince || "MB",
      notes: patient?.notes || "",
      userEmail: userEmail,
    },
    enableReinitialize: true,
    validationSchema: patientValidationSchema(t),
    onSubmit: async (values) => {
      setConfirmModal(true);
      const { noPhoneAcknowledgement, ...patientValues } = values;
      const formattedValues = formatPatientFormValues(patientValues);
      setPatientValues(formattedValues);
    },
  });
  const [patientValues, setPatientValues] = useState(formik.initialValues);
  const formikRef = useRef(formik);

  // Update patientRef when formik values change
  useEffect(() => {
    if (
      JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
    ) {
      patientRef.current = formik.values;
    }
  }, [formik.values, formik.initialValues, patientRef]);

  // when defined, use patientRef to populate formik values on initial load
  useEffect(() => {
    if (patientRef.current && !fetching) {
      formikRef.current.setValues(patientRef.current);
    }
  }, [patientRef, fetching]);

  const handleQRCodeRead = (qrJSON) => {
    const scannedPatientValues = mapScannedCardToPatientValues(qrJSON);
    const dob = DateTime.fromFormat(scannedPatientValues.dob, "yyyy-MM-dd");

    formik.setValues((prevValues) => ({
      ...prevValues,
      firstName: scannedPatientValues.firstName,
      lastName: scannedPatientValues.lastName,
      dob: dob?.isValid ? dob : null,
      registrationNumber: scannedPatientValues.registrationNumber,
      healthCardNumber: scannedPatientValues.healthCardNumber,
      healthCardProvince: "MB",
      preferredLanguage:
        scannedPatientValues.preferredLanguage || prevValues.preferredLanguage,
    }));
  };

  const handleOpenPhone = (event) => {
    setAnchorElPhone(event.currentTarget);
  };

  const handleClosePhone = () => {
    setAnchorElPhone(null);
  };

  const handleUpdateSuccess = () => {
    navigate("/");
  };

  const handleCreateSuccess = (createdPatient) => {
    if (createdPatient?.activated) {
      setOpenActivatedDialog(true);
      return;
    }
    navigate("/");
  };

  const phoneFields = ["primaryPhone", "secondaryPhone"];

  const clearFieldErrors = (fields) => {
    fields.forEach((name) => {
      formik.setFieldValue(name, "", false);
      formik.setFieldError(name, undefined);
      formik.setFieldTouched(name, false, false);
    });
  };

  const handleNoPhoneAcknowledgementChange = (e) => {
    const checked = e.target.checked;
    formik.setFieldValue("noPhoneAcknowledgement", checked, true);
    if (!checked) return;
    clearFieldErrors(phoneFields);
    formik.setFieldError("noPhoneAcknowledgement", undefined);
    formik.setFieldTouched("noPhoneAcknowledgement", false, false);
  };

  const standardFieldProps = {
    variant: "standard",
  };

  const field = (config) => ({
    ...standardFieldProps,
    ...config,
  });

  const demographicNameFields = [
    field({
      id: "firstName",
      label: t("patientForm.firstNameLabel"),
      type: "text",
      slotProps: {
        htmlInput: { maxLength: 30 },
      },
    }),
    field({
      id: "lastName",
      label: t("patientForm.lastNameLabel"),
      type: "text",
      slotProps: {
        htmlInput: { maxLength: 75 },
      },
    }),
  ];

  const demographicDateFields = [
    field({
      id: "dob",
      label: t("patientForm.dobLabel"),
      type: "date",
      sx: { ...styles.inputTextField, width: "100%" },
      datePickerSx: { ...styles.datePicker, width: "100%" },
    }),
  ];

  const demographicLocationFields = [
    field({
      id: "address",
      label: t("patientForm.addressLabel"),
      type: "text",
      slotProps: {
        htmlInput: { maxLength: 75 },
      },
    }),
    field({
      id: "city",
      label: t("patientForm.cityLabel"),
      type: "text",
      slotProps: {
        htmlInput: { maxLength: 30 },
      },
    }),
    field({
      id: "postalCode",
      label: t("patientForm.postalCodeLabel"),
      type: "text",
      onChange: (event, form) => {
        form.setFieldValue("postalCode", event.target.value.toUpperCase());
      },
    }),
    field({
      id: "province",
      label: t("patientForm.provinceLabel"),
      type: "select",
      options: provinceOptions,
    }),
  ];

  const demographicFieldMap = Object.fromEntries(
    [
      ...demographicNameFields,
      ...demographicDateFields,
      ...demographicLocationFields,
    ].map((field) => [field.id, field])
  );

  const demographicFieldRows = [
    ["firstName", "lastName", "dob"],
    ["address", "city"],
    ["postalCode", "province"],
  ];

  const healthCardNumberValue = formik.values.healthCardNumber ?? "";
  const isManitobaHealthCard = formik.values.healthCardProvince === "MB";
  const showInfantHealthCardHelper =
    isManitobaHealthCard &&
    isUnderOneYearOld(formik.values.dob) &&
    !isAllZeroPhin(healthCardNumberValue);
  const healthCardFields = [
    field({
      id: "healthCardProvince",
      label: t("patientForm.healthCardProvinceLabel"),
      type: "select",
      options: provinceOptions,
      onChange: (event, form) => {
        const selectedProvince = event.target.value;
        if (selectedProvince !== "MB") {
          form.setFieldValue("registrationNumber", "");
        }
        form.handleChange(event);
      },
    }),
    field({
      id: "healthCardNumber",
      label: t("patientForm.healthCardNumberLabel"),
      type: "text",
      showHelperTextWithError: true,
      helperText: showInfantHealthCardHelper
        ? t("patientForm.healthCardNumberHelperText")
        : undefined,
    }),
    field({
      id: "registrationNumber",
      label: t("patientForm.registrationNumberLabel"),
      type: "text",
      required: isManitobaHealthCard,
      disabled: !isManitobaHealthCard,
    }),
  ];

  const healthCardFieldMap = Object.fromEntries(
    healthCardFields.map((field) => [field.id, field])
  );
  const healthCardFieldRows = [
    ["healthCardProvince", "healthCardNumber", "registrationNumber"],
  ];

  const contactFields = [
    field({
      id: "preferredLanguage",
      label: t("patientForm.preferredLanguageLabel"),
      type: "select",
      options: [
        { value: "en", label: t("languages.english") },
        { value: "fr", label: t("languages.french") },
      ],
    }),
    field({
      id: "email",
      label: t("patientForm.emailLabel"),
      type: "email",
      slotProps: {
        htmlInput: { maxLength: 75 },
      },
    }),
    field({
      id: "primaryPhone",
      label: t("patientForm.phoneNumberLabel"),
      type: "tel",
      required: false,
      disabled: formik.values.noPhoneAcknowledgement,
      slotProps: {
        htmlInput: { maxLength: 14 },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleOpenPhone}
                disabled={formik.values.noPhoneAcknowledgement}
                sx={{ p: 0 }}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        },
      },
    }),
    field({
      id: "secondaryPhone",
      label: t("patientForm.secondaryPhoneNumberLabel"),
      type: "tel",
      required: false,
      disabled: formik.values.noPhoneAcknowledgement,
      slotProps: { htmlInput: { maxLength: 14 } },
    }),
  ];

  const contactFieldMap = Object.fromEntries(
    contactFields.map((field) => [field.id, field])
  );
  const contactFieldRows = [
    ["preferredLanguage", "email"],
    ["primaryPhone", "secondaryPhone"],
  ];

  const showNoPhoneAcknowledgementError =
    !formik.values.noPhoneAcknowledgement &&
    Boolean(formik.errors.noPhoneAcknowledgement) &&
    (formik.submitCount > 0 ||
      formik.touched.primaryPhone ||
      formik.touched.secondaryPhone ||
      formik.touched.noPhoneAcknowledgement);

  const renderDemographicField = (fieldId) => {
    const fieldConfig = demographicFieldMap[fieldId];
    if (!fieldConfig) return null;

    return fieldConfig.type === "date" ? (
      <FormikDatePicker formik={formik} field={fieldConfig} />
    ) : (
      <FormikTextField
        formik={formik}
        name={fieldConfig.id}
        field={fieldConfig}
      />
    );
  };

  const renderHealthField = (fieldId) => {
    const fieldConfig = healthCardFieldMap[fieldId];
    if (!fieldConfig) return null;
    return (
      <FormikTextField
        formik={formik}
        name={fieldConfig.id}
        field={fieldConfig}
      />
    );
  };

  const renderContactField = (fieldId) => {
    const fieldConfig = contactFieldMap[fieldId];
    if (!fieldConfig) return null;
    return (
      <>
        <FormikTextField
          formik={formik}
          name={fieldConfig.id}
          field={fieldConfig}
        />
        {fieldId === "primaryPhone" && (
          <Popover
            open={Boolean(anchorElPhone)}
            anchorEl={anchorElPhone}
            onClose={handleClosePhone}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Typography variant="body2" sx={{ p: 1, maxWidth: "200px" }}>
              {t("patientForm.primaryPhoneNumberHelperText")}
            </Typography>
          </Popover>
        )}
      </>
    );
  };

  return (
    <Box sx={styles.background}>
      <ActivatePatientModal
        open={openActivatedDialog}
        onClose={() => {
          setOpenActivatedDialog(false);
          navigate("/");
        }}
      />
      <DuplicatePHINModal
        open={openDuplicatePHINDialog}
        onClose={() => setOpenDuplicatePHINDialog(false)}
      />
      {fetching ? (
        <CircularProgress sx={styles.loading} />
      ) : (
        <Box
          component="form"
          sx={styles.formWrapper}
          onSubmit={formik.handleSubmit}
        >
          <PatientInfoHeader
            onBackClick={() => navigate("/")}
            title={t(titleString)}
            allowQRcode={!patient}
            onQrSuccess={handleQRCodeRead}
          />
          <Stack spacing={2}>
            {/* DEMOGRAPHICS */}
            <Card sx={styles.cardBackground}>
              <Stack spacing={2} sx={styles.cardContentStack}>
                <Typography sx={styles.cardTitle}>
                  {t("patientForm.demographics")}
                </Typography>
                {demographicFieldRows.map((rowFields, rowIndex) => (
                  <Stack
                    key={`demographic-row-${rowIndex}`}
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems="stretch"
                  >
                    {rowFields.map((fieldId) => (
                      <Box
                        key={fieldId}
                        sx={{ flex: 1, minWidth: 0, display: "flex" }}
                      >
                        {renderDemographicField(fieldId)}
                      </Box>
                    ))}
                  </Stack>
                ))}
              </Stack>
            </Card>
            {/* HEALTH CARD INFORMATION */}
            <Card sx={styles.cardBackground}>
              <Stack spacing={2} sx={styles.cardContentStack}>
                <Typography sx={styles.cardTitle}>
                  {t("patientForm.healthCardInformation")}
                </Typography>
                {healthCardFieldRows.map((rowFields, rowIndex) => (
                  <Stack
                    key={`health-row-${rowIndex}`}
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems="stretch"
                  >
                    {rowFields.map((fieldId) => (
                      <Box
                        key={fieldId}
                        sx={{ flex: 1, minWidth: 0, display: "flex" }}
                      >
                        {renderHealthField(fieldId)}
                      </Box>
                    ))}
                  </Stack>
                ))}
              </Stack>
            </Card>
            {/* CONTACT INFORMATION */}
            <Card sx={styles.cardBackground}>
              <Stack spacing={2} sx={styles.cardContentStack}>
                <Typography sx={styles.cardTitle}>
                  {t("patientForm.contactInformation")}
                </Typography>
                {showNoPhoneAcknowledgementError && (
                  <Alert
                    severity="error"
                    sx={{ ...styles.contactAlert, textAlign: "left" }}
                  >
                    {formik.errors.noPhoneAcknowledgement}
                  </Alert>
                )}
                {contactFieldRows.map((rowFields, rowIndex) => (
                  <Stack
                    key={`contact-row-${rowIndex}`}
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems="stretch"
                  >
                    {rowFields.map((fieldId) => (
                      <Box
                        key={fieldId}
                        sx={{ flex: 1, minWidth: 0, display: "flex" }}
                      >
                        {renderContactField(fieldId)}
                      </Box>
                    ))}
                  </Stack>
                ))}
                <FormControlLabel
                  sx={styles.contactCheckboxLabel}
                  control={
                    <Checkbox
                      checked={formik.values.noPhoneAcknowledgement}
                      onChange={handleNoPhoneAcknowledgementChange}
                      sx={styles.contactCheckbox}
                    />
                  }
                  label={
                    <Typography sx={styles.contactCheckboxText}>
                      <Box component="span" sx={styles.contactCheckboxBold}>
                        {t("patientForm.noPhoneAcknowledgementBold")}
                      </Box>
                      {t("patientForm.noPhoneAcknowledgementRest")}
                    </Typography>
                  }
                />
              </Stack>
            </Card>
            <Box sx={styles.buttonBox}>
              <Button
                aria-label={t("buttons.cancel")}
                variant="text"
                type="button"
                onClick={() => navigate("/")}
              >
                {t("buttons.cancel")}
              </Button>
              <Button
                aria-label={t("buttons.submit")}
                type="submit"
                variant="contained"
                sx={{ width: { xs: "100%", sm: "150px" } }}
              >
                {t("buttons.submit")}
              </Button>
            </Box>
          </Stack>
        </Box>
      )}

      <ConfirmEditModal
        open={confirmModal}
        onCloseModal={() => setConfirmModal(false)}
        patientValues={patientValues}
        setOpenDuplicatePHINDialog={setOpenDuplicatePHINDialog}
        onCreateSuccess={handleCreateSuccess}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </Box>
  );
};

export default PatientForm;
